import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { Accordion, Button, Icon, tokens, Typography } from "@vintecc/components";
import { faArrowLeft } from "@vintecc/pro-light-svg-icons";
import { faBug } from "@vintecc/pro-solid-svg-icons/faBug";
import { AxiosError } from "axios";
import { ErrorInfo } from "react";
import { parseStackTrace } from "./stackTraceUtils";
import { StackTraceViewer } from "./StackTraceViewer";
import { CommonPluralTranslationKey, CommonTranslationKey, useCommonTranslation } from "@/services/culture/common.translations";

interface ErrorDisplayProps {
  error?: {
    error?: Error | null;
    info?: { componentStack: string } | null;
    reset?: () => void;
  };
  errorInfo?: ErrorInfo | null;
}

export const ErrorDisplay = ({ error, errorInfo }: ErrorDisplayProps) => {
  const { t: translateCommon } = useCommonTranslation();
  const t = (key: Exclude<CommonTranslationKey, CommonPluralTranslationKey>) => translateCommon(key);

  const getMessage = (error: ErrorDisplayProps["error"]) => {
    if (error?.error instanceof AxiosError) {
      const axiosError = error.error as AxiosError;
      const statusText = axiosError.response?.statusText;
      const statusCode = axiosError.response?.status;
      const data = axiosError.response?.data;

      let message = `${statusText} ${statusCode}`;
      if (typeof data === "string") {
        message += `: ${data}`;
      }
      return message;
    }
    return error?.error?.message || t("errors.unexpectedError");
  };

  const handleCopyDetails = () => {
    const sections: string[] = [];

    sections.push(`Error: ${error?.error?.toString() || message}`);

    if (componentStack) {
      sections.push(`Component Stack:\n${componentStack}`);
    }

    if (error?.error instanceof Error && error.error.stack) {
      sections.push(`Stack Trace:\n${error.error.stack}`);
    }

    const details = sections.join("\n\n");
    copyToClipboard(details);
  };

  const message = getMessage(error!);
  const onBack = () => window.history.back();
  const onHome = () => window.location.assign("/");
  const { copyToClipboard } = useCopyToClipboard();

  const componentStack = error?.info?.componentStack ?? errorInfo?.componentStack;
  const stackTrace = error?.error instanceof Error ? error.error.stack : undefined;

  const stackFrames = stackTrace ? parseStackTrace(stackTrace) : [];
  const componentStackFrames = componentStack ? parseStackTrace(componentStack) : [];

  return (
    <div className="flex flex-col h-full relative">
      <div className="flex flex-col items-center justify-center gap-8 h-full">
        <Icon icon={faBug} size="10x" color={tokens.color.pink[400]} />
        <Typography variant="h2" as="h1">
          {t("errors.crashTitle")}
        </Typography>
        <Typography variant="p">{message}</Typography>
        <div className="flex items-center gap-4">
          {error?.reset && (
            <Button variant="outline" onClick={error.reset}>
              {t("common.tryAgain")}
            </Button>
          )}
          <Button variant="plain" startIcon={<Icon icon={faArrowLeft} />} onClick={onBack}>
            {t("common.goBack")}
          </Button>
          <Button onClick={onHome}>{t("common.goToHome")}</Button>
        </div>
      </div>

      {(componentStack || stackTrace) && (
        <div className="w-full bg-white border-t border-gray-200 p-2 px-4 absolute bottom-0 shadow-xl shadow-gray-400 overflow-hidden">
          <Accordion
            title={
              <Typography variant="strong" color={tokens.color.grey[600]}>
                {t("errors.viewTechnicalDetails")}
              </Typography>
            }
          >
            <div className="py-4 border-t border-gray-200">
              <div className="flex flex-col gap-6">
                {stackTrace && <StackTraceViewer frames={stackFrames} title={t("errors.stackTrace")} />}
                {componentStack && <StackTraceViewer frames={componentStackFrames} title={t("errors.componentStack")} />}

                <Button variant="outline" onClick={handleCopyDetails} className="self-start">
                  {t("errors.copyErrorDetails")}
                </Button>
              </div>
            </div>
          </Accordion>
        </div>
      )}
    </div>
  );
};
